import { useMemo, useState } from 'react';
import type { Drama } from '@/types/content';

interface EpisodeGuideProps {
  drama: Drama;
}

export default function EpisodeGuide({ drama }: EpisodeGuideProps) {
  const latestNo = drama.episodes[drama.episodes.length - 1]?.no ?? drama.updatedEpisodes;
  const [query, setQuery] = useState('');
  const [openNo, setOpenNo] = useState<number | null>(latestNo);

  const list = useMemo(() => {
    const kw = query.trim().toLowerCase();
    if (!kw) return drama.episodes;
    return drama.episodes.filter(
      (ep) =>
        String(ep.no).includes(kw) ||
        ep.title.toLowerCase().includes(kw) ||
        ep.summary.toLowerCase().includes(kw) ||
        ep.date.includes(kw),
    );
  }, [drama.episodes, query]);

  return (
    <section id="episodes" className="w-full scroll-mt-24 border-b border-background-200 bg-background-50 py-12 md:py-16">
      <div className="mx-auto w-full max-w-content px-4 md:px-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="mb-2 flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-primary-600">
              <span className="h-px w-8 bg-primary-400" aria-hidden="true" />
              Episode Guide
            </p>
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">分集剧情介绍</h2>
            <p className="mt-3 max-w-2xl text-sm leading-7 text-foreground-600">
              按连续剧集更新顺序排列，《{drama.title}》每一集讲什么一目了然。对关键反转我们会做留白，把悬念留给你自己。
            </p>
          </div>

          <div className="flex w-full max-w-xs items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2 focus-within:border-primary-400">
            <i className="ri-search-line text-foreground-400" aria-hidden="true" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="输入集号或关键词"
              aria-label="搜索分集"
              className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
          </div>
        </div>

        <div className="mt-8 overflow-hidden rounded-lg border border-background-200">
          {list.length === 0 && (
            <p className="bg-background-50 px-4 py-10 text-center text-sm text-foreground-500">
              没有找到匹配的集数，试试输入「第 3 集」或剧情关键词。
            </p>
          )}

          {list.map((episode) => {
            const isOpen = openNo === episode.no;
            const isLatest = episode.no === latestNo;
            return (
              <article
                key={episode.no}
                className={`border-b border-background-200 last:border-b-0 ${
                  isOpen ? 'bg-background-100' : 'bg-background-50'
                }`}
              >
                <h3>
                  <button
                    type="button"
                    aria-expanded={isOpen}
                    onClick={() => setOpenNo(isOpen ? null : episode.no)}
                    className="flex w-full cursor-pointer items-center gap-4 px-4 py-4 text-left transition-colors hover:bg-background-100 md:px-6"
                  >
                    <span
                      className={`flex h-11 w-11 shrink-0 flex-col items-center justify-center rounded-md text-xs ${
                        isOpen ? 'bg-primary-500 text-background-50' : 'bg-background-200 text-foreground-700'
                      }`}
                    >
                      <span className="font-display text-sm leading-none">{episode.no}</span>
                      <span className="mt-0.5 text-[10px] opacity-80">集</span>
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium text-foreground-950 md:text-base">{episode.title}</span>
                        {isLatest && (
                          <span className="rounded-md bg-accent-100 px-2 py-0.5 text-[11px] text-accent-900">
                            最新更新
                          </span>
                        )}
                      </span>
                      <span className="mt-1 block text-xs text-foreground-500">上线时间 {episode.date}</span>
                    </span>
                    <i
                      className={`shrink-0 text-lg text-foreground-400 transition-transform duration-300 ${
                        isOpen ? 'ri-subtract-line rotate-180' : 'ri-add-line'
                      }`}
                      aria-hidden="true"
                    />
                  </button>
                </h3>
                {isOpen && (
                  <div className="px-4 pb-5 md:px-6 md:pl-[72px]">
                    <p className="max-w-3xl text-sm leading-7 text-foreground-700">{episode.summary}</p>
                  </div>
                )}
              </article>
            );
          })}
        </div>

        <p className="mt-5 flex items-start gap-2 text-xs leading-6 text-foreground-500">
          <i className="ri-information-line mt-0.5 text-foreground-400" aria-hidden="true" />
          当前展示的是《{drama.title}》已更新的 {drama.updatedEpisodes} 集中的最近 {drama.episodes.length} 集剧情介绍，
          完整集数顺序会在该剧更新后持续补充。
        </p>
      </div>
    </section>
  );
}